import { Link } from "react-router";
import { SiteCopy } from "../copy";
import { CONTACT_HREF, ROUTES } from "../links";
import { WebinarStrip } from "../widgets/employers";
import { usePageTitle } from "./usePageTitle";
import { PageFaq } from "../widgets/PageFaq";

const FAQ: [string, string][] = [
  [
    "What does &ldquo;Ontario launch pricing&rdquo; mean?",
    "Abi models federal tax plus Ontario, Quebec, British Columbia and Nunavut today. The rest of the provinces and territories are being built. If you live somewhere we haven&rsquo;t finished, we&rsquo;ll tell you before you pay rather than handing you a plan built on the wrong tax.",
  ],
  [
    "Is this financial advice?",
    "Abi produces projections and recommendations from your own data, and it doesn&rsquo;t hold a dealer licence. For anything that needs one &mdash; buying a security, buying insurance &mdash; you still deal with a licensed person, and Abi tells you exactly what to ask them. Where your plan shows a gap we can introduce you to a partner, and we tell you when that introduction pays us. The gap always comes from your numbers first.",
  ],
  [
    "What if I don&rsquo;t want to connect my bank?",
    "You can type everything in by hand. It takes longer and the plan is less accurate, because twelve months of real transactions tell us things no questionnaire will. But it works.",
  ],
  [
    "How accurate is a forty-year projection, really?",
    "Less accurate the further out it goes, and we show you that rather than hiding it. The near-term numbers come from your actual balances and rates. The long-term ones rest on a 6% growth assumption and on you following the plan. The honest use of a forty-year projection is comparing two of them, which is exactly what we show you.",
  ],
  ["Can I cancel?", "Any time, from your settings. Your data goes with the account."],
  [
    "Does it work on a phone?",
    "Yes, and the plan is readable on one. The charts have room to breathe on a larger screen, so most people set up on a laptop and check in from their phone afterwards.",
  ],
];

const INCLUDES = [
  "Your full lifetime plan, rebuilt whenever your data changes",
  "Both plans side by side &mdash; yours and the do-nothing one",
  "Cash flow, net worth, debt, retirement and priorities",
  "Decision tools for a home, a car, a job change or a new expense",
  "Ask Abi anything, answered against your own numbers",
  "Unlimited account connections",
];

const row = { display: "flex", justifyContent: "space-between", fontSize: 13.5, padding: "5px 0" };

/** Pricing (the prototype's `data-s="pricing"`). */
export function PricingPage() {
  usePageTitle("Pricing");
  return (
    <div className="spage on" data-s="pricing">
      <header className="hero" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <SiteCopy as="h1" k="pricingEmp.h2" />
          <p className="lede">No tiers, no add-ons, and never a percentage of what anyone owns.</p>
        </div>
      </header>
      <section className="band tight" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="ptier rv" id="ptier">
            <div className="tcard">
              <SiteCopy as="p" className="mono" k="pricingEmp.indiv" />
              <SiteCopy as="div" className="tamt" k="pricingEmp.indivP" />
              <SiteCopy as="p" className="tsub" k="pricingEmp.indivSub" />
              <Link className="sbtn" to={ROUTES.start}>
                Start your plan
              </Link>
            </div>
            <div className="tcard hot">
              <SiteCopy as="p" className="mono" k="pricingEmp.team" />
              <SiteCopy as="div" className="twas" k="pricingEmp.teamWas" />
              <SiteCopy as="div" className="tamt" k="pricingEmp.teamP" />
              <SiteCopy as="p" className="tsub" k="pricingEmp.teamSub" />
              <SiteCopy as="p" className="tnote" k="pricingEmp.teamNote" />
              <a className="sbtn p" href={CONTACT_HREF}>
                Book a demo of Abi for your team
              </a>
            </div>
          </div>
        </div>
      </section>

      <WebinarStrip />

      <section className="band">
        <div className="wrap">
          <div className="price">
            <p className="mono">Ontario launch pricing</p>
            <div className="amt">$9.99</div>
            <p className="per">per month, CAD &middot; $120 a year</p>
            <div
              style={{
                marginTop: 20,
                padding: 15,
                borderRadius: 13,
                background: "rgba(255,255,255,.035)",
                border: "1px solid var(--s-line)",
                textAlign: "left",
              }}
            >
              <p className="mono" style={{ marginBottom: 10 }}>
                What this replaces
              </p>
              <div style={row}>
                <span style={{ color: "var(--s-ink2)" }}>A one-off fee-only financial plan</span>
                <b className="tnum">$2,000&ndash;$4,000</b>
              </div>
              <div style={row}>
                <span style={{ color: "var(--s-ink2)" }}>1% of assets, on $500k, every year</span>
                <b className="tnum">$5,000/yr</b>
              </div>
              <div
                style={{
                  ...row,
                  borderTop: "1px solid var(--s-line)",
                  marginTop: 5,
                }}
              >
                <span style={{ color: "var(--s-ink2)" }}>Abi</span>
                <b className="tnum" style={{ color: "#34D7A8" }}>
                  $120/yr
                </b>
              </div>
            </div>
            <Link className="sbtn p big" style={{ marginTop: 22, width: "100%" }} to={ROUTES.start}>
              Start your plan
            </Link>
            <p style={{ fontSize: 12.5, color: "var(--s-ink3)", marginTop: 12 }}>
              Thirty days to change your mind. Cancel in two clicks and your data goes with the
              account.
            </p>
            <ul>
              {INCLUDES.map((t) => (
                <li key={t} dangerouslySetInnerHTML={{ __html: t }} />
              ))}
            </ul>
          </div>
          <div
            className="card tall"
            style={{ maxWidth: 560, margin: "28px auto 0", textAlign: "left" }}
          >
            <p className="mono" style={{ marginBottom: 14 }}>
              How we make money
            </p>
            <div className="secrow" style={{ paddingTop: 0 }}>
              <span className="ic">&#8226;</span>
              <div>
                <b>Your subscription</b>
                <p>
                  $9.99 a month. This is the main thing, and it is the reason Abi can rank your
                  priorities instead of a product shelf.
                </p>
              </div>
            </div>
            <div className="secrow">
              <span className="ic">&#8226;</span>
              <div>
                <b>Referrals, when your plan shows a real gap</b>
                <p>
                  If Abi finds you are underinsured or paying over the odds on a mortgage, it can
                  introduce you to a partner, and we may be paid for that introduction. Two rules we
                  hold ourselves to: the gap is identified from your numbers before any partner is
                  named, and you are told when a referral earns us something.
                </p>
              </div>
            </div>
            <div className="secrow">
              <span className="ic">&#8226;</span>
              <div>
                <b>Never a percentage of your savings</b>
                <p>
                  We do not manage your money and we do not take a slice of it. Your plan costs the
                  same whether you have $20,000 or $2 million.
                </p>
              </div>
            </div>
          </div>
          <div className="shead" style={{ marginTop: 72 }}>
            <p className="mono">For organisations</p>
            <h2>Abi as an employee benefit</h2>
            <p>
              Financial stress doesn&rsquo;t stay at home. Abi is available to employers who want to
              give their people a real plan rather than a lunch-and-learn.
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <Link className="sbtn big" to={ROUTES.employers}>
              See how it works for teams
            </Link>
          </div>
          <div className="shead" style={{ marginTop: 76 }}>
            <h2>Questions people ask first</h2>
          </div>
          <div className="wrap" style={{ maxWidth: 800, padding: 0 }}>
            {FAQ.map(([q, a]) => (
              <details className="faq" key={q}>
                <summary dangerouslySetInnerHTML={{ __html: q }} />
                <p dangerouslySetInnerHTML={{ __html: a }} />
              </details>
            ))}
          </div>
        </div>
      </section>
      <PageFaq page="pricing" />
    </div>
  );
}
